import React, { useRef, useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useT } from '../lib/i18n';
import { Camera, X, Image as ImageIcon, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = {
  fr: {
    before: 'Avant',
    after: 'Après',
    measurement: 'Mesures',
    defect: 'Défauts à corriger',
  },
  en: {
    before: 'Before',
    after: 'After',
    measurement: 'Measurements',
    defect: 'Defects',
  },
};

export function PhotoGallery({ submission, onUpdate }) {
  const { addPhotoToSubmission, removePhotoFromSubmission, updatePhotoCaption } = useData();
  const { lang } = useT();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState('before');
  const [lightbox, setLightbox] = useState(null);

  const photos = submission?.photos || [];
  const labels = CATEGORIES[lang] || CATEGORIES.fr;

  const onFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 8 * 1024 * 1024) {
          toast.error(`${file.name} > 8 MB`);
          continue;
        }
        await addPhotoToSubmission(submission.id, file, category);
      }
      toast.success(lang === 'fr' ? '✓ Photo(s) ajoutée(s)' : '✓ Photo(s) added', { duration: 1500 });
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.message || (lang === 'fr' ? 'Erreur d\u2019envoi' : 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onRemove = async (photoId) => {
    if (!window.confirm(lang === 'fr' ? 'Supprimer cette photo ?' : 'Delete this photo?')) return;
    await removePhotoFromSubmission(submission.id, photoId);
    if (onUpdate) onUpdate();
  };

  const onCaptionBlur = async (photo, caption) => {
    if (caption === photo.caption) return;
    await updatePhotoCaption(submission.id, photo.id, { caption });
    if (onUpdate) onUpdate();
  };

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-500">
          {lang === 'fr' ? 'Photos de chantier' : 'Site photos'}
        </div>
        <span className="text-xs text-gray-500">{photos.length}</span>
      </div>

      <div className="grid grid-cols-4 gap-1 mb-3">
        {Object.entries(labels).map(([key, label]) => (
          <button
            key={key}
            data-testid={`photo-cat-${key}`}
            onClick={() => setCategory(key)}
            className={`min-h-[40px] rounded-lg border-2 text-[11px] font-bold transition-all ${
              category === key
                ? 'border-[#7A1F2B] bg-[#7A1F2B] text-white'
                : 'border-[#E5DCC9] text-gray-600'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <input
        ref={fileRef}
        data-testid="photo-input"
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        hidden
        onChange={onFileChange}
      />
      <button
        data-testid="photo-upload-btn"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="btn-outline w-full gap-2 border-dashed mb-4 disabled:opacity-50"
      >
        {uploading ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
        {uploading
          ? (lang === 'fr' ? 'Téléversement…' : 'Uploading…')
          : (lang === 'fr' ? `Ajouter une photo (${labels[category]})` : `Add photo (${labels[category]})`)}
      </button>

      {photos.length === 0 ? (
        <div className="text-center text-gray-400 text-xs py-6">
          {lang === 'fr' ? 'Aucune photo encore. Prenez le chantier en photo.' : 'No photos yet. Snap the site.'}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(labels).map(([key, label]) => {
            const inCat = photos.filter((p) => p.category === key);
            if (!inCat.length) return null;
            return (
              <div key={key}>
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">
                  {label} <span className="text-gray-400">({inCat.length})</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {inCat.map((p) => (
                    <div key={p.id} className="relative group">
                      <button
                        type="button"
                        onClick={() => setLightbox(p)}
                        className="block w-full aspect-square rounded-lg overflow-hidden bg-[#FAF7F2] border border-[#E5DCC9]"
                      >
                        <img src={p.url} alt={p.caption || label} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                      <button
                        data-testid={`photo-remove-${p.id}`}
                        onClick={() => onRemove(p.id)}
                        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-red-700 opacity-0 group-hover:opacity-100 sm:opacity-100"
                      >
                        <Trash2 size={13} />
                      </button>
                      {p.caption && (
                        <div className="text-[10px] text-gray-600 truncate mt-1">{p.caption}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center"
          >
            <X size={20} />
          </button>
          <div className="max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt="" className="w-full max-h-[80vh] object-contain rounded-lg" />
            <input
              data-testid="photo-caption-input"
              defaultValue={lightbox.caption || ''}
              onBlur={(e) => onCaptionBlur(lightbox, e.target.value)}
              placeholder={lang === 'fr' ? 'Description (optionnelle)' : 'Caption (optional)'}
              className="field mt-3 !bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}
